import React from 'react';

import { FormattedDate, FormattedMessage, useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphWorkItem_AtlassianSearchPage$key } from './__generated__/searchGraphWorkItem_AtlassianSearchPage.graphql';
import { messages } from './messages';

const nameContainer = xcss({
	whiteSpace: 'nowrap',
});

const statusAndDateContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

const getStatusLozengeAppearance = (label: string) => {
	label = label.replace(/[\s]+/g, '').toLowerCase();
	switch (true) {
		case ['onhold'].includes(label):
			return 'neutral'; // gray

		case ['solved', 'complete'].includes(label):
			return 'success'; // green

		case ['open', 'offtrack'].includes(label):
			return 'danger'; // red

		case ['new', 'atrisk'].includes(label):
			return 'warning'; // yellow

		case ['pending', 'ontrack'].includes(label):
			return 'information'; // blue
	}
	return 'information'; // blue for custom status labels
};

export const SearchGraphWorkItem = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphWorkItem_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphWorkItem_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				url
				excerpt
				lastModifiedDate
				permissionLevel
				entity {
					... on ExternalWorkItem {
						assignee {
							thirdPartyUser {
								id
								name
							}
						}
						createdBy {
							thirdPartyUser {
								id
								name
							}
						}
						dueDate
						workItemProject {
							id
							name
						}
						status
						subtype
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { lastModifiedDate, url, permissionLevel, entity, excerpt, title } = data;
	const { assignee, createdBy, dueDate, workItemProject, status, subtype } = entity || {};

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getStatusAndDate = () => {
		if (!status) {
			return null;
		}
		const statusLabel = status.replace(/[-_]+/g, ' ');

		const statusLozenge = (
			<Lozenge appearance={getStatusLozengeAppearance(statusLabel)}>{statusLabel}</Lozenge>
		);
		const dateLozenge = dueDate && (
			<Lozenge>
				<FormattedDate value={dueDate} month="short" day="numeric" />
			</Lozenge>
		);

		return (
			<Box xcss={statusAndDateContainerStyles}>
				{dueDate ? (
					<FormattedMessage {...messages.for} values={{ statusLozenge, dateLozenge }} />
				) : (
					statusLozenge
				)}
			</Box>
		);
	};

	const getAssigneeOrCreator = () => {
		let actionLabel: string;
		const fullName = assignee?.thirdPartyUser?.name || createdBy?.thirdPartyUser?.name || '';
		const firstName = fullName?.split(' ')?.[0];
		if (assignee?.thirdPartyUser?.name) {
			actionLabel = formatMessage(messages.isAssigned, {
				firstName: firstName,
				isAssigned: firstName ? 'internal' : 'external',
			});
		} else {
			actionLabel = formatMessage(messages.created, {
				firstName: firstName,
				created: firstName ? 'internal' : 'external',
			});
		}

		return (
			<Inline space="space.025" alignBlock="center">
				<ProfileAvatar name={fullName} />
				<Box xcss={nameContainer}>{actionLabel}</Box>
			</Inline>
		);
	};

	const subtypeMap: Record<string, any> = {
		task: messages.taskLabel,
		default_task: messages.taskLabel,
		epic: messages.epicLabel,
		bug: messages.bugLabel,
		story: messages.storyLabel,
		milestone: messages.milestoneLabel,
		section: messages.sectionLabel,
		approval: messages.approvalLabel,
		problem: messages.problemLabel,
		incident: messages.incidentLabel,
		question: messages.questionLabel,
		work_item: messages.workItemLabel,
		issue: messages.issueLabel,
		ticket: messages.ticketLabel,
		requested_item: messages.requestedItemLabel,
		catalog_item: messages.catalogItemLabel,
		other: messages.workItemLabel,
	};

	const label = formatMessage(
		subtypeMap[subtype?.toLowerCase() ?? 'other'] || messages.workItemLabel,
	);

	const headerMetadataParts = [
		label,
		['project', workItemProject?.name],
		['last-modified', lastModifiedComponent],
		['padlock', padlockIcon],
		['status', getStatusAndDate()],
		['person', getAssigneeOrCreator()],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={processExcerpt(excerpt, true)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
